import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { isTauri, isPopupWindow, initializePopupWindowStatus } from '@/lib/utils/tauri';
import { cn } from '@/lib/utils';
import { TauriTitleBarBasic, TAURI_TITLE_BAR_HEIGHT } from './TauriTitleBar';
import {
  UPDATE_BANNER_HEIGHT,
  TauriUpdateBannerSlot,
  useUpdateBannerVisible,
} from '@/components/shared/UpdateBanner';

interface TauriTitleBarShellProps {
  children: ReactNode;
  className?: string;
}

export function TauriTitleBarShell({ children, className }: TauriTitleBarShellProps) {
  const isMobile = useIsMobile();
  const [isPopup, setIsPopup] = useState(isPopupWindow());
  const hasInitializedPopup = useRef(false);

  useEffect(() => {
    if (hasInitializedPopup.current) return;
    hasInitializedPopup.current = true;

    if (isTauri()) {
      initializePopupWindowStatus().then(result => {
        if (result !== isPopup) {
          setIsPopup(result);
        }
      });
    }
  }, [isPopup]);

  const showTitleBar = isTauri() && !isMobile && !isPopup;
  const showUpdateBanner = useUpdateBannerVisible() && showTitleBar;
  const chromeHeight =
    (showTitleBar ? TAURI_TITLE_BAR_HEIGHT : 0) +
    (showUpdateBanner ? UPDATE_BANNER_HEIGHT : 0);
  const contentStyle = {
    paddingTop: chromeHeight,
    ['--tauri-titlebar-offset' as string]: '0px',
    ['--tauri-content-height' as string]: chromeHeight > 0
      ? `calc(100vh - ${chromeHeight}px)`
      : '100vh',
    ['--tauri-shell-offset' as string]: `${chromeHeight}px`,
  };

  return (
    <>
      {showTitleBar && <TauriTitleBarBasic />}
      {showTitleBar && <TauriUpdateBannerSlot />}
      <div className={cn(className)} style={contentStyle}>
        {children}
      </div>
    </>
  );
}
